import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DoctorMessagesService,
  type GetApiDoctorMessagesThreadsByLabVisitByLabVisitIdResponses,
  type GetApiDoctorMessagesThreadsResponses,
  type GetApiDoctorMessagesThreadsByIdReplySuggestionResponses,
  type PostApiDoctorMessagesThreadsByIdReplyResponses,
} from "@/lib/api_client";
import { getAuthHeaders, impersonateHeaders } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export type DoctorMessageThreadRow =
  GetApiDoctorMessagesThreadsResponses[200]["threads"][number];
export type DoctorThreadDetail =
  GetApiDoctorMessagesThreadsByLabVisitByLabVisitIdResponses[200];
export type DoctorThreadMessage = DoctorThreadDetail["messages"][number];

/** Server-side maxLimit for this route — asking for more is clamped anyway. */
const PAGE_SIZE = 200;

export const doctorMessageThreadsKey = (status?: "open" | "closed") =>
  ["doctor-message-threads", status ?? "all"] as const;

async function fetchThreads(
  status?: "open" | "closed",
  headers: Record<string, string> = getAuthHeaders(),
): Promise<DoctorMessageThreadRow[]> {
  const threads: DoctorMessageThreadRow[] = [];
  let offset = 0;
  for (;;) {
    const res = await DoctorMessagesService.getApiDoctorMessagesThreads({
      headers,
      query: {
        ...(status ? { status } : {}),
        limit: String(PAGE_SIZE),
        offset: String(offset),
      },
    });
    if (!res.data) throw new Error("Kunne ikke hente meldinger");
    threads.push(...res.data.threads);
    offset += res.data.threads.length;
    if (res.data.threads.length === 0 || threads.length >= res.data.total)
      break;
  }
  return threads;
}

/**
 * All doctor-chat threads (staff view: every customer; BHT staff are scoped
 * server-side). Ordered by last activity, with customer name, who wrote last
 * and message count. The staff list carries no message text.
 */
export const useDoctorMessageThreads = (options?: {
  status?: "open" | "closed";
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: doctorMessageThreadsKey(options?.status),
    queryFn: () => fetchThreads(options?.status),
    enabled: options?.enabled !== false,
  });

/**
 * One patient's threads with their text, for the patient page. Answers only
 * an impersonation token, and only one minted for this patient is used. The
 * key keeps the `doctor-message-threads` prefix so a reply refreshes it, and
 * its own segment so it never shares the text-less staff rows' cache entry.
 */
export const usePatientDoctorMessageThreads = (authUserId: string) => {
  const { impersonateToken, impersonatingUserId } = useAuth();
  const token = impersonatingUserId === authUserId ? impersonateToken : null;
  return useQuery({
    queryKey: ["doctor-message-threads", "patient", authUserId] as const,
    queryFn: () => fetchThreads(undefined, impersonateHeaders(token!)),
    enabled: !!token,
  });
};

/**
 * How many threads match, for tab badges. The endpoint already returns a true
 * `total`, so one row is enough: paging through every thread just to render a
 * number would fetch hundreds of rows on the customer list.
 *
 * The key keeps the `doctor-message-threads` prefix so the reply mutation's
 * invalidation refreshes the badge too.
 */
export const useDoctorMessageThreadCount = (options?: {
  status?: "open" | "closed";
  authUserId?: string;
  /** Only threads where the customer wrote the latest message. */
  awaitingReply?: boolean;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      "doctor-message-threads",
      "count",
      options?.status ?? "all",
      options?.authUserId ?? null,
      options?.awaitingReply ?? false,
    ] as const,
    queryFn: async (): Promise<number> => {
      const res = await DoctorMessagesService.getApiDoctorMessagesThreads({
        headers: getAuthHeaders(),
        query: {
          ...(options?.status ? { status: options.status } : {}),
          ...(options?.authUserId ? { authUserId: options.authUserId } : {}),
          ...(options?.awaitingReply ? { awaitingReply: "true" as const } : {}),
          limit: "1",
          offset: "0",
        },
      });
      if (!res.data) throw new Error("Kunne ikke hente meldinger");
      return res.data.total;
    },
    enabled: options?.enabled !== false,
  });

/** Full thread with all messages, for replying from the customer page.
 *  This and the two mutations below need the impersonation token. */
export const useDoctorThreadByLabVisit = (labVisitId: string | null) => {
  const { impersonateToken } = useAuth();
  return useQuery({
    queryKey: ["doctor-message-thread", labVisitId],
    queryFn: async (): Promise<DoctorThreadDetail | null> => {
      const res =
        await DoctorMessagesService.getApiDoctorMessagesThreadsByLabVisitByLabVisitId(
          {
            path: { labVisitId: labVisitId! },
            headers: impersonateHeaders(impersonateToken!),
          },
        );
      if (res.data) return res.data;
      // 404 just means no conversation has been opened for this visit.
      if (res.response?.status !== 404) throw new Error("Kunne ikke hente samtalen");
      return null;
    },
    enabled: !!impersonateToken && !!labVisitId,
  });
};

type ReplyResult = PostApiDoctorMessagesThreadsByIdReplyResponses[201];
export type DoctorReplySuggestionState =
  GetApiDoctorMessagesThreadsByIdReplySuggestionResponses[200];

export const doctorReplySuggestionKey = (threadId: string) =>
  ["doctor-reply-suggestion", threadId] as const;

/**
 * The draft prepared for the thread's latest open question. The server queues
 * generation when the customer writes, so this is usually `ready` on first
 * fetch; while it is `pending` the query polls until the draft lands.
 */
export const useDoctorReplySuggestion = (threadId: string | null) => {
  const { impersonateToken } = useAuth();
  return useQuery({
    queryKey: doctorReplySuggestionKey(threadId ?? ""),
    queryFn: async (): Promise<DoctorReplySuggestionState> => {
      const res =
        await DoctorMessagesService.getApiDoctorMessagesThreadsByIdReplySuggestion(
          {
            path: { id: threadId! },
            headers: impersonateHeaders(impersonateToken!),
          },
        );
      if (!res.data) throw new Error("Kunne ikke hente forslaget");
      return res.data;
    },
    enabled: !!impersonateToken && !!threadId,
    refetchInterval: (query) =>
      query.state.data?.status === "pending" ? 2000 : false,
  });
};

/** Ask for a fresh draft; the GET above polls until it is ready. */
export const useRequestDoctorReplySuggestion = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation({
    mutationFn: async (threadId: string) => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res =
        await DoctorMessagesService.postApiDoctorMessagesThreadsByIdReplySuggestion(
          {
            path: { id: threadId },
            headers: impersonateHeaders(impersonateToken),
          },
        );
      if (!res.data) {
        if (res.response?.status === 409) {
          throw new Error("Ingen ubesvart melding akkurat nå");
        }
        throw new Error("Kunne ikke lage et forslag");
      }
      return res.data;
    },
    onSuccess: (_data, threadId) => {
      qc.invalidateQueries({ queryKey: doctorReplySuggestionKey(threadId) });
    },
  });
};

/** Doctor reply, optionally closing the thread. */
export const useReplyDoctorMessageThread = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation({
    mutationFn: async (vars: {
      threadId: string;
      text: string;
      close?: boolean;
    }): Promise<ReplyResult> => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res = await DoctorMessagesService.postApiDoctorMessagesThreadsByIdReply(
        {
          path: { id: vars.threadId },
          body: { text: vars.text, ...(vars.close ? { close: true } : {}) },
          headers: impersonateHeaders(impersonateToken),
        },
      );
      if (!res.data) throw new Error("Kunne ikke svare");
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doctor-message-threads"] });
      qc.invalidateQueries({ queryKey: ["doctor-message-thread"] });
      // The server drops the draft once the question is answered.
      qc.invalidateQueries({ queryKey: ["doctor-reply-suggestion"] });
    },
  });
};

/** Close or reopen a thread without sending the customer anything. */
export const useSetDoctorThreadStatus = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation({
    mutationFn: async (vars: {
      threadId: string;
      status: "open" | "closed";
    }) => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res =
        await DoctorMessagesService.postApiDoctorMessagesThreadsByIdStatus({
          path: { id: vars.threadId },
          body: { status: vars.status },
          headers: impersonateHeaders(impersonateToken),
        });
      if (!res.data) throw new Error("Kunne ikke endre status");
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doctor-message-threads"] });
      qc.invalidateQueries({ queryKey: ["doctor-message-thread"] });
    },
  });
};
